import { lazy, Suspense } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';

import { selectUser } from 'store';

import Loader from 'shared/Loader';

import {
  useAuthBootstrapStatus,
  useBootstrapQuerySettledOnce
} from 'contexts/authBootstrapContext';
import { useAppSelector } from 'hooks';
import { useMyPrograms } from 'hooks/useMyPrograms/useMyPrograms';
import { useSelectFeatureStatus } from 'hooks/useSelectFeatureStatus';
import { FeatureFlag } from 'utils/enums';
import { lazyRetry } from 'utils/lazy';
import { PathName } from './paths';

import { RequireProgram, StardustPostOnboardingGuard } from './guards';

const Weight = lazy(() => lazyRetry(() => import('pages/Weight')));
const MentalHealth = lazy(() => lazyRetry(() => import('pages/MentalHealth')));
const WomensHRT = lazy(() => lazyRetry(() => import('pages/WomensHRT')));
const MensTRT = lazy(() => lazyRetry(() => import('pages/MensTRT')));
const Checkout = lazy(() => lazyRetry(() => import('pages/Checkout')));
const UpgradePlan = lazy(() => lazyRetry(() => import('pages/UpgradePlan')));
const MigrateToBundlePlan = lazy(() => lazyRetry(() => import('pages/MigrateToBundlePlan')));
const MigrateToBundlePlanFromWM = lazy(() =>
  lazyRetry(() => import('pages/MigrateToBundlePlanFromWM'))
);
const CurrentWeight = lazy(() => lazyRetry(() => import('pages/CurrentWeight')));
const PrescriptionStatus = lazy(() => lazyRetry(() => import('pages/PrescriptionStatus')));
const Survey = lazy(() => lazyRetry(() => import('pages/Survey')));
const SelectPrescriptions = lazy(() => lazyRetry(() => import('pages/SelectPrescriptions')));
const DeviceStatus = lazy(() => lazyRetry(() => import('pages/DeviceStatus')));
const AddMeasurement = lazy(() => lazyRetry(() => import('pages/AddMeasurement')));
const MigrateToPlan = lazy(() => lazyRetry(() => import('pages/MigrateToPlan')));
const PostOnboarding = lazy(() => lazyRetry(() => import('containers/PostOnboarding')));
const PostOnboardingAlt = lazy(() => lazyRetry(() => import('containers/PostOnboardingAlt')));
const PostOnboardingWH = lazy(() => lazyRetry(() => import('containers/PostOnboardingWH')));
const PostOnboardingTRT = lazy(() =>
  lazyRetry(() => import('containers/PostOnboardingWH/PostOnboardingTRT'))
);
const PostOnboardingMensTRT = lazy(() =>
  lazyRetry(() => import('containers/PostOnboardingMensTRT'))
);
const MigrateFromTirzepatide = lazy(() => lazyRetry(() => import('pages/MigrateFromTirzepatide')));
const MigrateFromSemaglutide = lazy(() => lazyRetry(() => import('pages/MigrateFromSemaglutide')));
const MigrateToTT = lazy(() => lazyRetry(() => import('containers/MigrateToTT')));
const MigrateToWM = lazy(() => lazyRetry(() => import('containers/MigrateToWM')));
const MedicareConversion = lazy(() => lazyRetry(() => import('containers/MedicareConversion')));
const ReonboardingSummary = lazy(() => lazyRetry(() => import('pages/ReonboardingSummary')));
const MedicationConfirmation = lazy(() => lazyRetry(() => import('pages/MedicationConfirmation')));

const MigrateToBundlePlanGuard = () => {
  const { weightManagement } = useMyPrograms();
  const isDisabledMigrateToBundlePlan = useSelectFeatureStatus('migrateToBundlePlan');
  const isExtendedReOnboardingWMFlowEnabled = useSelectFeatureStatus('reOnboardingWM', 'enabled');
  const isAbleToChangeToTheBundle =
    !isDisabledMigrateToBundlePlan ||
    isExtendedReOnboardingWMFlowEnabled ||
    weightManagement?.details?.requiredAppointment?.reason === 'bundled-offer';

  if (!isAbleToChangeToTheBundle) {
    return <Navigate to={PathName.NotFound} replace />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      {!weightManagement ? <MigrateToBundlePlan /> : <MigrateToBundlePlanFromWM />}
    </Suspense>
  );
};

export const IntakeFormGuard = () => {
  const { search } = useLocation();
  const { useNewPostOnboarding } = useAppSelector(selectUser);
  const { weightManagement } = useMyPrograms();
  const { myPrograms } = useAuthBootstrapStatus();
  const hasProgramsAnswer = useBootstrapQuerySettledOnce(myPrograms);

  // `useNewPostOnboarding` is written true in exactly one place — inside the new container, after
  // its own weight-management gate has already passed — and is never written false or backfilled.
  // So a patient the container turned away before that write had no route back in, and anything
  // that turned them away once excluded them for good. An Active weight-management program is the
  // condition the container gates on anyway, so this reads it directly rather than depending on a
  // flag whose absence may only mean the container never got far enough to set it.
  if (useNewPostOnboarding || weightManagement) {
    return <Navigate to={{ pathname: PathName.PostOnboarding, search }} replace />;
  }

  // The programs slice reads empty both before /my-programs resolves and when the patient really
  // has no weight-management program, so an absent program is not an answer yet. Deciding on it
  // during a cold load onto this route hands the legacy container to a patient who belongs in the
  // new one — the same mistake one level up from the flag. Settled includes failed, so a fetch
  // that never arrives falls through to legacy rather than holding the route open. This observes
  // the same argless bootstrap query as RequireProgram; calling it with an argument would create a
  // different cache entry that this cannot see.
  //
  // Latched, because the container below is what fires the next `/my-programs`: asking "is a
  // request in flight?" on every render made this guard replace its own child with a loader the
  // moment that child loaded, then remount it, forever (LMD-2942). The latch only opens on a
  // quiet query, so a patient whose weight-management program is still in flight is held here
  // rather than handed the legacy container — a refetch is not evidence that she has no program.
  if (!hasProgramsAnswer) {
    return <Loader isVisible />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      <PostOnboarding />
    </Suspense>
  );
};

const MigrateToTTGuard = () => {
  const { isInsurancePatient, isTTPatient } = useMyPrograms();

  if (isTTPatient || isInsurancePatient) {
    return <Navigate to={PathName.Weight} replace />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      <MigrateToTT />
    </Suspense>
  );
};

export const MigrateToWMGuard = () => {
  const { hadOrHasWeightManagement, weightManagement } = useMyPrograms();

  if (hadOrHasWeightManagement) {
    /*
     * Not `/not-found`. This route is reached by a redirect rather than by a link, and the redirect
     * that reaches it is re-applied on every route, so a 404 here was re-entered immediately and
     * the two bounced the patient between them until the browser throttled the history API —
     * leaving a blank page, because `<Navigate>` renders nothing. Both of these land on a page the
     * patient can act from, and neither redirects back.
     */
    return <Navigate to={weightManagement ? PathName.Weight : PathName.Home} replace />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      <MigrateToWM />
    </Suspense>
  );
};

export const MensTRTIntakeGuard = () => {
  const isDynamic = useFlag(FeatureFlag.EnablePomifDynamicTrt);
  return (
    <RequireProgram program="TRT" fallback={<Navigate to={PathName.Home} replace />}>
      <StardustPostOnboardingGuard category="mens-health">
        <Suspense fallback={<Loader isVisible />}>
          {isDynamic ? <PostOnboardingTRT /> : <PostOnboardingMensTRT />}
        </Suspense>
      </StardustPostOnboardingGuard>
    </RequireProgram>
  );
};

export const getMainLayoutProgramRoutes = () => (
  <>
    <Route
      element={
        <RequireProgram program="WM" fallback={<Navigate to={PathName.Home} replace />}>
          <Weight />
        </RequireProgram>
      }
      path={PathName.Weight}
    />
    <Route
      element={
        <RequireProgram program="MH" fallback={<Navigate to={PathName.Home} replace />}>
          <MentalHealth />
        </RequireProgram>
      }
      path={PathName.MentalHealth}
    />
    <Route
      element={
        <RequireProgram program="WH" fallback={<Navigate to={PathName.Home} replace />}>
          <WomensHRT />
        </RequireProgram>
      }
      path={PathName.WomensHRT}
    />
    <Route
      element={
        <RequireProgram program="TRT" fallback={<Navigate to={PathName.Home} replace />}>
          <MensTRT />
        </RequireProgram>
      }
      path={PathName.MensTRT}
    />
    <Route element={<Navigate to={PathName.Weight} replace />} path="/weight-management" />
    <Route element={<MigrateToBundlePlanGuard />} path={PathName.MigrateToBundlePlan} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <UpgradePlan />
        </Suspense>
      }
      path={PathName.UpgradePlan}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <Checkout />
        </Suspense>
      }
      path={PathName.Checkout}
    />
  </>
);

export const getProtectedProgramRoutes = () => (
  <>
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <ReonboardingSummary />
        </Suspense>
      }
      path={PathName.ReonboardingSummary}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MedicationConfirmation />
        </Suspense>
      }
      path={PathName.MedicationConfirmation}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <CurrentWeight />
        </Suspense>
      }
      path={PathName.CurrentWeight}
    />
    <Route element={<PrescriptionStatus />} path={PathName.Prescriptions} />
    <Route element={<Survey />} path={PathName.Survey} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <SelectPrescriptions />
        </Suspense>
      }
      path={PathName.SelectPrescriptions}
    />
    <Route
      element={
        <RequireProgram program="WM" fallback={<Navigate to={PathName.Home} replace />}>
          <DeviceStatus />
        </RequireProgram>
      }
      path={PathName.DeviceStatus}
    />
    <Route element={<MigrateToPlan />} path={PathName.MigrateToPlan} />
    <Route
      element={
        <RequireProgram program="WM" fallback={<Navigate to={PathName.Home} replace />}>
          <AddMeasurement />
        </RequireProgram>
      }
      path={PathName.AddMeasurement}
    />
    <Route
      element={
        <StardustPostOnboardingGuard>
          <IntakeFormGuard />
        </StardustPostOnboardingGuard>
      }
      path={PathName.IntakeForm}
    />
    <Route path={PathName.IntakeFormMensTRT} element={<MensTRTIntakeGuard />} />
    <Route
      element={
        <StardustPostOnboardingGuard>
          <Suspense fallback={<Loader isVisible />}>
            <PostOnboardingAlt />
          </Suspense>
        </StardustPostOnboardingGuard>
      }
      path={PathName.PostOnboarding}
    />
    <Route
      path={PathName.PostOnboardingWH}
      element={
        <RequireProgram program="WH" fallback={<Navigate to={PathName.Home} replace />}>
          <StardustPostOnboardingGuard category="women-health">
            <Suspense fallback={<Loader isVisible />}>
              <PostOnboardingWH />
            </Suspense>
          </StardustPostOnboardingGuard>
        </RequireProgram>
      }
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MigrateFromTirzepatide />
        </Suspense>
      }
      path={PathName.MoveFromTirzepatide}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MigrateFromSemaglutide />
        </Suspense>
      }
      path={PathName.MoveFromSemaglutide}
    />
    <Route element={<MigrateToTTGuard />} path={PathName.MigrateToTT} />
    <Route element={<MigrateToWMGuard />} path={PathName.MigrateToWM} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MedicareConversion />
        </Suspense>
      }
      path={PathName.MedicareConversion}
    />
  </>
);
